import Trpc from 'digicel-kaizen-library/lib/tripica-library';
import { isEqual } from 'lodash';
import config from '../../config';
import { createSelector } from 'reselect';
import { getFormattedOptions, getSecondaryCharacteristicsForOptions } from '../../lib/optionsFormatter';
import { sortByDataVoiceSMS } from '../../lib/sort-options';
import {
  availableCreditableOptionsSelector,
  localCreditableOptionsSelector,
  roamingCreditableOptionsSelector,
} from '../AvailableOption/selectors';
import { currentOfferSelector } from '../Subscription';
import type { StateType } from '../reducers';

export const newOfferSelector = state => state.newOffer;

export const newOfferPriceSelector = state => {
  return state.newOffer.price?.taxIncludedAmount || 0;
};

export const newOfferPeriodicitySelector = state => state.newOffer.plan?.periodicity;

export const newOfferPlanCharacteristicsSelector = state => state.newOffer.plan?.characteristics;

export const newOfferOptionsSelector = state => state.newOffer.options || [];

export const isNewOfferValidSelector = (state): boolean => {
  const options = newOfferOptionsSelector(state);
  const atLeastOneCreditableOption = options.some(({ type }) => type !== 'DEFAULT');
  // When the user explicitly selects a zero-value option (e.g. 0Go DATA), they are
  // opting out of that service type. The Tripica relationship validation doesn't
  // understand this: other options' DEPENDENCY relationships still point to specific
  // DATA ouids that the synthetic zero option can never match → false negative.
  // Bypass relationship validation entirely when a zero option is present.
  const hasZeroOption = options.some(opt => isEffectivelyZeroOption(opt));
  // Ensure every option has a relationships array before calling Tripica validation
  const safeOptions = options.map(opt => ({
    ...opt,
    relationships: opt.relationships || [],
  }));
  const validRelationships = hasZeroOption || Trpc.areOptionsRelationshipsValid(safeOptions);

  return atLeastOneCreditableOption && validRelationships;
};

export const newOfferOptionsMappedByCreditableServiceSelector = state => {
  let options = newOfferOptionsSelector(state);
  const planCharacteristicsEntries = Object.entries(newOfferPlanCharacteristicsSelector(state) || {});

  let nbEmptyOptions = 0;
  planCharacteristicsEntries.forEach(([type, planCharacteristic]) => {
    if (options.some(option => option.type === type)) {
      return;
    }

    nbEmptyOptions++;
    options = [
      ...options,
      {
        characteristic: planCharacteristic,
        type,
      },
    ];
  });

  // When local DATA is zero and no roaming data option exists, add a zero
  // ROA_DATA placeholder so PlanSummary renders "0Go" for roaming data.
  const dataSpecs = config.roamingDataServiceSpecs || [];
  if (dataSpecs.length > 0) {
    const hasRoamingData = options.some(o =>
      dataSpecs.includes(o.type) || o.type === 'ROA_DATA',
    );
    const localDataOption = options.find(o => o.type === 'DATA');
    const isLocalDataZero = localDataOption?.characteristic?.value === '0';

    if (!hasRoamingData && isLocalDataZero) {
      options = [
        ...options,
        {
          type: 'ROA_DATA',
          characteristic: { unit: 'VOLUME', value: '0' },
          price: {
            taxIncludedAmount: 0,
            dutyFreeAmount: 0,
            taxRate: 12.5,
            currencyCode: 'EUR',
            percentage: 0,
            priceType: 'RECURRING',
          },
        },
      ];
    }
  }

  // no creditable options and no service options
  if (nbEmptyOptions === planCharacteristicsEntries.length && options.length === planCharacteristicsEntries.length) {
    return null;
  }

  return getFormattedOptions(options);
};

export const secondaryCharacteristicsOptionsSelector = state => {
  const options = newOfferOptionsSelector(state);

  return getSecondaryCharacteristicsForOptions(options);
};

// An option is "effectively zero" if it is:
// - a client-side synthetic zero option (no ouid), OR
// - a backend-auto-set zero option (has ouid, but characteristic value is '0')
// In both cases it means "nothing selected for this type" and should not count as a real selection.
const isEffectivelyZeroOption = (option) =>
  option.characteristic?.value === '0';

export const isNewOfferSameAsCurrentSelector = (state): boolean => {

  const currentOfferOptions = currentOfferSelector(state).options
    .filter(option => !isEffectivelyZeroOption(option))
    .map(option => option.ouid)
    .sort();

  const newOfferOptions = newOfferOptionsSelector(state)
    .filter(option => !isEffectivelyZeroOption(option))
    .map(option => option.ouid)
    .sort();

  return isEqual(currentOfferOptions, newOfferOptions);
};

const buildZeroValuesSelector = (optionsSelector) =>
  createSelector(
    [newOfferPlanCharacteristicsSelector, newOfferPeriodicitySelector, optionsSelector],
    (planCharacteristics, planPeriodicity, creditableOptionsAvailable) => {
      if (!creditableOptionsAvailable || Object.keys(creditableOptionsAvailable).length === 0) {
        return [];
      }
      return (Object.values(creditableOptionsAvailable): any)
    .map((options: Object[]) => {
      const optionsType = options[0].type;
      // We prevent the 0 values option to be added for SMS and VOICE.
      if (optionsType !== 'SMS' && optionsType !== 'VOICE') {
        let zeroValuesOptionOuid;
        let zeroValuesOptionName;
        if (optionsType === 'DATA' && config.zeroDataOptionOuid) {
          zeroValuesOptionOuid = config.zeroDataOptionOuid;
          zeroValuesOptionName = config.zeroDataOptionOuid;
        } else if (/^ROA{1,2}\d_DATA$/.test(optionsType) && config.zeroRoamingDataOptionOuid) {
          zeroValuesOptionOuid = config.zeroRoamingDataOptionOuid;
          zeroValuesOptionName = config.zeroRoamingDataOptionOuid;
        } else if (optionsType === 'TO_ZONED_VOICE' && config.zeroRoamingVoiceOptionOuid) {
          zeroValuesOptionOuid = config.zeroRoamingVoiceOptionOuid;
          zeroValuesOptionName = config.zeroRoamingVoiceOptionOuid;
        }
        const zeroValuesOption = {
          // Only DATA has a real catalogued 0Go product; other types keep a synthetic zero (no ouid)
          ...(zeroValuesOptionOuid && zeroValuesOptionName
            ? { ouid: zeroValuesOptionOuid, name: zeroValuesOptionName }
            : {}),
          characteristic: (planCharacteristics && planCharacteristics[optionsType])
            || { unit: options[0]?.characteristic?.unit || options[0]?.characteristics?.[0]?.unit || 'String', value: '0' },
          periodicity: planPeriodicity || 'MONTH',
          price: { taxIncludedAmount: 0, dutyFreeAmount: 0, taxRate: 12.5, currencyCode: 'EUR', percentage: 0, priceType: 'RECURRING' },
          type: optionsType,
        };
        return [zeroValuesOption, ...options];
      } else {
        return [...options];
      }
    })
    .sort((creditableOptions1, creditableOptions2) =>
      sortByDataVoiceSMS(creditableOptions1[0].type, creditableOptions2[0].type)
    );
    }
  );

export const getCreditableOptionsWithPlanWithZeroValues: (state: StateType) => Object[][] =
  buildZeroValuesSelector(availableCreditableOptionsSelector);

export const getLocalCreditableOptionsWithPlanWithZeroValues: (state: StateType) => Object[][] =
  buildZeroValuesSelector(localCreditableOptionsSelector);

export const getRoamingCreditableOptionsWithPlanWithZeroValues: (state: StateType) => Object[][] =
  buildZeroValuesSelector(roamingCreditableOptionsSelector);
